// What a paste into the writing becomes: paragraphs, and headings kept as
// headings. Copied from a web page or a document, a heading arrives as an
// <h1>–<h6> in the clipboard's HTML; typed in Markdown, as a "# " line. Before
// this, both landed as plain paragraphs (Niki's "Rooms versus a web", 9/30).

export interface PastedPart {
  kind: 'text' | 'heading'
  text: string
  /** A heading's level: h1 → 1, h2 → 2, h3–h6 → 3 (and "#", "##", "###…" in Markdown). */
  level?: 1 | 2 | 3
}

const levelOf = (n: number): 1 | 2 | 3 => (n <= 1 ? 1 : n === 2 ? 2 : 3)

const HEADING = /^H[1-6]$/
const LEAF_BLOCK = /^(P|LI|BLOCKQUOTE|PRE|H[1-6]|DIV|DT|DD|FIGCAPTION|TD|TH)$/
const BLOCKS = 'p, li, blockquote, pre, h1, h2, h3, h4, h5, h6, div, dt, dd, figcaption, td, th, ul, ol'
const SKIP = /^(SCRIPT|STYLE|TEMPLATE|META|LINK|TITLE)$/
const MARKDOWN_HEADING = /^(#{1,6})\s+(\S.*)$/

const squash = (s: string) => s.replace(/\s+/g, ' ').trim()

/** The clipboard's HTML as paragraphs and headings, if it has any headings; else null. */
function fromHtml(html: string): PastedPart[] | null {
  if (!html || !/<h[1-6][\s>]/i.test(html)) return null
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  const parts: PastedPart[] = []

  const walk = (node: ParentNode) => {
    // Inline text between blocks (loose words, a <span>) is a paragraph of its own.
    let inline = ''
    const flush = () => {
      const text = squash(inline)
      if (text) parts.push({ kind: 'text', text })
      inline = ''
    }
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        inline += child.textContent ?? ''
        continue
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue
      const el = child as Element
      const tag = el.tagName.toUpperCase()
      if (SKIP.test(tag)) continue
      const holdsBlocks = el.querySelector(BLOCKS) !== null
      if (LEAF_BLOCK.test(tag) && !holdsBlocks) {
        flush()
        const text = squash(el.textContent ?? '')
        if (text) parts.push(HEADING.test(tag) ? { kind: 'heading', text, level: levelOf(Number(tag[1])) } : { kind: 'text', text })
      } else if (holdsBlocks) {
        flush()
        walk(el)
      } else {
        inline += tag === 'BR' ? ' ' : (el.textContent ?? '')
      }
    }
    flush()
  }

  walk(tpl.content)
  return parts.length ? parts : null
}

/** Plain text: paragraphs at blank lines; a one-line paragraph written "# Like this" is a heading. */
function fromText(text: string): PastedPart[] {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const m = !p.includes('\n') && MARKDOWN_HEADING.exec(p)
      return m ? { kind: 'heading' as const, text: squash(m[2]), level: levelOf(m[1].length) } : { kind: 'text' as const, text: squash(p) }
    })
}

export function pastedParts(html: string, text: string): PastedPart[] {
  return fromHtml(html) ?? fromText(text)
}
